import { useCallback, useMemo, useState, useRef } from "react";

import { Formik, ErrorMessage } from "formik";
import { Handshake } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import * as Yup from "yup";

import { AssignmentAdvancedCreatorSearchWrapper } from "@/features/assignments/components/AssignmentAdvancedCreatorSearchWrapper";
import { CampaignSelect } from "@/features/campaigns";
import { CreatorInviteFields } from "@/features/creators/components/CreatorInviteFields";
import { CreatorSelect } from "@/features/creators/components/CreatorSelect";
import {
  clearCreatorInviteFields,
  extractCreatorDataFromFormValues,
} from "@/features/creators/utils/creatorInviteFormUtils";
import { apiSlice } from "app/apiSlice";
import CreatorVisibleIndicator from "components/Forms/components/CreatorVisibleIndicator";
import { NavLink } from "components/NavLink";
import { showToast } from "components/Toasts/helpers/showToast";
import { useConfirmLoseUnsaved } from "hooks/useConfirmLoseUnsaved";

interface CreateAssignmentModalProps {
  campaign_id?: string;
  creator_id?: string;
  isOpen: boolean;
  onClose: () => void;
}

type AssignmentFormValues = {
  creator_id: string;
  campaign_id: string;
  rate: string;
  status: string;
  nextPayoutDate: string;
  agencyCreatorName: string;
  inviteCreatorName: string;
  inviteCreatorEmail: string;
  inviteCreatorPhone: string;
  inviteCreatorCountry: string;
  inviteCreatorIsAgency: boolean;
  inviteCreatorEnableNotifications: boolean;
};

interface DuplicateCheckResult {
  isDuplicate: boolean;
  assignmentId?: string | null;
  error?: any;
}

export const CreateAssignmentModal = ({
  campaign_id,
  creator_id,
  isOpen,
  onClose,
}: CreateAssignmentModalProps) => {
  // API hooks
  const [
    createAssignment,
    {
      isLoading: isCreatingAssignment,
      isSuccess: isAssignmentCreated,
      data: createdAssignmentData,
      reset: resetCreateAssignment,
    },
  ] = apiSlice.useCreateAssignmentMutation();

  const [
    inviteCreator,
    { isLoading: isCreatingCreator, reset: resetInviteCreator },
  ] = apiSlice.useInviteCreatorMutation();

  const [checkForExistingAssignments] = apiSlice.useLazyGetAssignmentsQuery();

  // Track validation state outside Formik
  const [isValidatingDuplicate, setIsValidatingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  // Advanced Creator Search modal state
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);

  // Simple unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Ref to store Formik's resetForm function
  const formikResetRef = useRef<(() => void) | null>(null);

  // Move isInviteMode to React state
  const [isInviteMode, setIsInviteMode] = useState(false);

  const isLoading = isCreatingAssignment || isCreatingCreator;

  // Navigation blocking when there are unsaved changes - handles both React Router and browser navigation
  useConfirmLoseUnsaved({
    when: hasUnsavedChanges && isOpen && !isAssignmentCreated,
    message: "You have unsaved changes. Are you sure you want to leave?",
  });

  const initialValues = useMemo<AssignmentFormValues>(
    () => ({
      creator_id: creator_id || "",
      campaign_id: campaign_id || "",
      rate: "",
      status: "",
      nextPayoutDate: "",
      agencyCreatorName: "",
      inviteCreatorName: "",
      inviteCreatorEmail: "",
      inviteCreatorPhone: "",
      inviteCreatorCountry: "",
      inviteCreatorIsAgency: null,
      inviteCreatorEnableNotifications: true,
    }),
    [creator_id, campaign_id],
  );

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges && !isAssignmentCreated) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to close this form? Your changes will be lost.",
      );
      if (!confirmed) {
        return;
      }
    }

    resetCreateAssignment();
    resetInviteCreator();
    setDuplicateError(null);
    setHasUnsavedChanges(false);
    onClose();
  }, [
    hasUnsavedChanges,
    isAssignmentCreated,
    resetCreateAssignment,
    resetInviteCreator,
    onClose,
  ]);

  const handleAddAnother = useCallback(() => {
    resetCreateAssignment();
    resetInviteCreator();
    setDuplicateError(null);
    setHasUnsavedChanges(false);
    // Reset the form to initial values
    if (formikResetRef.current) {
      formikResetRef.current();
    }
  }, [resetCreateAssignment, resetInviteCreator]);

  const checkForDuplicateAssignment = useCallback(
    async(
      creatorId: string,
      campaignId: string,
    ): Promise<DuplicateCheckResult> => {
      if (!creatorId || !campaignId) {
        return { isDuplicate: false };
      }

      try {
        setIsValidatingDuplicate(true);
        const checkResult = await checkForExistingAssignments({
          user_id: creatorId,
          campaign_id: campaignId,
          limit: 1,
          _source: "create assignment duplicate validation",
        });

        if (checkResult.data?.results?.length > 0) {
          const existingAssignment = checkResult.data.results[0];
          return {
            isDuplicate: true,
            assignmentId: existingAssignment?._id || null,
          };
        }

        return { isDuplicate: false };
      } catch (error) {
        console.error("Error checking for duplicate assignments:", error);
        // Don't block form submission on API errors
        return { isDuplicate: false, error };
      } finally {
        setIsValidatingDuplicate(false);
      }
    },
    [checkForExistingAssignments],
  );

  const validationSchema = Yup.lazy((values: any) => {
    // If creator_id is present, require only creator_id
    if (values && values.creator_id) {
      return Yup.object({
        creator_id: Yup.string().required("Creator is required"),
        campaign_id: Yup.string().required("Campaign is required"),
        rate: Yup.number().positive("Rate must be greater than 0"),
        status: Yup.string(),
        nextPayoutDate: Yup.date().nullable(),
        agencyCreatorName: Yup.string(),
        // inviteCreatorName: Yup.string(),
        // inviteCreatorEmail: Yup.string(),
        // inviteCreatorPhone: Yup.string(),
        // inviteCreatorCountry: Yup.string(),
        // inviteCreatorIsAgency: Yup.mixed(),
        // inviteCreatorEnableNotifications: Yup.boolean(),
      });
    }
    // Otherwise, require the invite fields
    return Yup.object({
      // creator_id: Yup.string(),
      campaign_id: Yup.string().required("Campaign is required"),
      rate: Yup.number().positive("Rate must be greater than 0"),
      status: Yup.string(),
      nextPayoutDate: Yup.date().nullable(),
      agencyCreatorName: Yup.string(),
      inviteCreatorName: Yup.string().required("Creator name is required"),
      inviteCreatorEmail: Yup.string()
        .required("Creator email is required")
        .email("Must be a valid email address"),
      inviteCreatorPhone: Yup.string(),
      inviteCreatorCountry: Yup.string().required("Country is required"),
      inviteCreatorIsAgency: Yup.mixed().required("Account type is required"),
      inviteCreatorEnableNotifications: Yup.boolean(),
    });
  });

  // Manual validation for duplicate assignments
  const validateSelectFields = useCallback(
    async(
      creatorId: string,
      campaignId: string,
      setFieldError: (field: string, message: string) => void,
      setFieldTouched: (field: string, touched: boolean) => void,
    ) => {
      if (!creatorId || !campaignId) {
        setDuplicateError(null);
        return;
      }

      const duplicateCheck = await checkForDuplicateAssignment(
        creatorId,
        campaignId,
      );

      if (duplicateCheck.isDuplicate) {
        const errorMessage = `__DUPLICATE_ASSIGNMENT__${duplicateCheck.assignmentId || "no-id"}`;
        setDuplicateError(errorMessage);
        // Set empty errors on both fields to trigger invalid styling without messages
        setFieldError("creator_id", " "); // Space to ensure truthy but invisible
        setFieldError("campaign_id", " ");
        // Mark both fields as touched so invalid styling displays
        setFieldTouched("creator_id", true);
        setFieldTouched("campaign_id", true);
      } else {
        // Clear any existing duplicate errors from both fields
        setDuplicateError(null);
        setFieldError("creator_id", "");
        setFieldError("campaign_id", "");
      }
    },
    [checkForDuplicateAssignment],
  );

  // Ref to track previous values for duplicate validation
  const prevValuesRef = useRef({ creator_id: "", campaign_id: "" });

  const handleCreateAssignment = useCallback(
    async(values: AssignmentFormValues, { setSubmitting }: any) => {
      let inviteResult: any = null;
      let creatorInvited = false;

      try {
        const assignmentData: any = {
          campaign_id: values.campaign_id,
          rate: parseFloat(values.rate),
          status: values.status,
          ...(values.nextPayoutDate && {
            nextPayoutDate: values.nextPayoutDate,
          }),
          ...(values.agencyCreatorName && {
            agencyCreatorName: values.agencyCreatorName,
          }),
        };

        if (isInviteMode) {
          try {
            // First, invite/create the creator
            const creatorData = extractCreatorDataFromFormValues(values);
            inviteResult = await inviteCreator(creatorData).unwrap();
            creatorInvited = true;

            // Use the newly created creator's ID for the assignment
            assignmentData.user_id = inviteResult?.result?._id;
          } catch (inviteError) {
            console.error("Error inviting creator:", inviteError);
            showToast({
              type: "error",
              message:
                "Failed to invite creator. Please check the creator details and try again.",
            });
            return; // Exit early if creator invitation fails
          }
        } else {
          // Use existing creator
          assignmentData.user_id = values.creator_id;
        }

        try {
          // Create the assignment
          await createAssignment(assignmentData).unwrap();
          setIsInviteMode(false);

          // Clear unsaved changes on successful submission
          setHasUnsavedChanges(false);

          // RTK Query will automatically set isSuccess and data
        } catch (assignmentError) {
          console.error("Error creating assignment:", assignmentError);

          if (isInviteMode && creatorInvited) {
            // Creator was invited successfully but assignment creation failed
            showToast({
              type: "error",
              message:
                "Creator was invited successfully, but failed to create assignment. You can create the assignment manually from the creator's page.",
            });
          } else {
            // Assignment creation failed for existing creator
            showToast({
              type: "error",
              message: "Failed to create assignment. Please try again.",
            });
          }
        }
      } catch (error) {
        // This catch block handles any unexpected errors
        console.error("Unexpected error:", error);
        showToast({
          type: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [createAssignment, inviteCreator, isInviteMode],
  );

  const validateForm = useCallback(
    (values: AssignmentFormValues) => {
      // Simple dirty check
      const hasContent = Object.entries(values).some(([key, value]) => {
        if (typeof value === "boolean") {
          return value !== initialValues[key as keyof AssignmentFormValues];
        }
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        return false;
      });

      if (hasContent !== hasUnsavedChanges && !isAssignmentCreated) {
        setHasUnsavedChanges(hasContent);
      }

      // Only validate duplicates for existing creators, not for invites
      if (!isInviteMode) {
        // Trigger duplicate validation when field values change (including programmatic changes)
        if (
          (values.creator_id !== prevValuesRef.current.creator_id
            || values.campaign_id !== prevValuesRef.current.campaign_id)
          && values.creator_id
          && values.campaign_id
        ) {
          prevValuesRef.current = {
            creator_id: values.creator_id,
            campaign_id: values.campaign_id,
          };

          // Trigger async validation (without setFieldError since we're in validate function)
          checkForDuplicateAssignment(
            values.creator_id,
            values.campaign_id,
          ).then((duplicateCheck) => {
            if (duplicateCheck.isDuplicate) {
              const errorMessage = `__DUPLICATE_ASSIGNMENT__${duplicateCheck.assignmentId || "no-id"}`;
              setDuplicateError(errorMessage);
            } else {
              setDuplicateError(null);
            }
          });
        } else if (!values.creator_id || !values.campaign_id) {
          setDuplicateError(null);
          prevValuesRef.current = {
            creator_id: values.creator_id,
            campaign_id: values.campaign_id,
          };
        }

        if (duplicateError) {
          return {
            creator_id: " ",
            campaign_id: " ",
          };
        }
      } else {
        // Clear duplicate errors in invite mode
        setDuplicateError(null);
      }

      return {};
    },
    [
      hasUnsavedChanges,
      isAssignmentCreated,
      checkForDuplicateAssignment,
      duplicateError,
      initialValues,
      isInviteMode,
    ],
  );

  // Extract creator select change handler
  const handleCreatorSelectChange = useCallback(
    (
      selectedOption: any,
      setFieldError: any,
      setFieldTouched: any,
      campaignId: string,
    ) => {
      const value = selectedOption?.value || "";
      if (value && campaignId) {
        validateSelectFields(value, campaignId, setFieldError, setFieldTouched);
      }
    },
    [validateSelectFields],
  );

  // Extract campaign select change handler
  const handleCampaignSelectChange = useCallback(
    (
      selectedOption: any,
      setFieldError: any,
      setFieldTouched: any,
      creatorId: string,
    ) => {
      const value = selectedOption?.value || "";
      if (creatorId && value) {
        validateSelectFields(creatorId, value, setFieldError, setFieldTouched);
      }
    },
    [validateSelectFields],
  );

  // Handler for switching to invite mode
  const handleToggleInviteMode = useCallback(
    (
      newIsInviteMode: boolean,
      setFieldValue: any,
      setFieldTouched: any,
      setFieldError: any,
    ) => {
      setIsInviteMode(newIsInviteMode);
      setFieldValue("creator_id", "");
      setFieldTouched("creator_id", false);
      clearCreatorInviteFields(setFieldValue, setFieldTouched, setFieldError);
      setFieldError("creator_id", "");
      setDuplicateError(null);
    },
    [],
  );

  // Determine if selects should show error state due to duplicate
  const hasDuplicateError = duplicateError?.startsWith(
    "__DUPLICATE_ASSIGNMENT__",
  );

  // Advanced Creator Search handlers
  const handleAdvancedSearchOpen = useCallback(() => {
    setIsAdvancedSearchOpen(true);
  }, []);

  const handleAdvancedSearchClose = useCallback(() => {
    setIsAdvancedSearchOpen(false);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      toggle={handleClose}
      className="create-assignment-modal"
      backdrop="static"
      keyboard={false}
      unmountOnClose={true}
      returnFocusAfterClose={true}
    >
      <ModalHeader toggle={handleClose} className="bg-body-secondary">
        <div className="d-flex align-items-center">
          <Handshake size={"1.2rem"} className="me-2" />
          {isAssignmentCreated ? "Assignment Created" : "Create Assignment"}
          {/* <span id="assignmentInfoTooltip" className="ms-2 text-muted">
            <Info size={".9rem"} />
          </span>
          <UncontrolledTooltip target="assignmentInfoTooltip">
            TODO
          </UncontrolledTooltip> */}
          {isLoading && <Spinner size="sm" className="ms-2" />}
        </div>
      </ModalHeader>
      <ModalBody>
        {isAssignmentCreated ? (
          <div className="text-center py-4 d-flex flex-column align-items-center">
            <div className="mb-4">
              <i
                className="fas fa-check-circle text-success"
                style={{ fontSize: "3rem" }}
              ></i>
            </div>
            <h4 className="mb-3">Assignment Created Successfully!</h4>
            <p className="text-muted mb-4">
              The assignment has been created successfully.
            </p>
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleCreateAssignment}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={false}
            validate={validateForm}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isSubmitting,
              resetForm,
              setFieldError,
              setFieldTouched,
              setFieldValue,
            }) => {
              // Store resetForm function in ref
              formikResetRef.current = resetForm;

              return (
                <>
                  <Form onSubmit={handleSubmit} id="assignment-form">
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="creator_id">Creator</Label>
                          {!isInviteMode ? (
                            <>
                              <CreatorSelect
                                name="creator_id"
                                isDisabled={
                                  isLoading || isSubmitting || !!creator_id
                                }
                                required={true}
                                campaign_id={values.campaign_id || campaign_id}
                                onChange={(selectedOption) =>
                                  handleCreatorSelectChange(
                                    selectedOption,
                                    setFieldError,
                                    setFieldTouched,
                                    values.campaign_id,
                                  )
                                }
                              />
                              <p className="text-muted small mt-2 mb-0">
                                Can&apos;t find the creator you&apos;re looking
                                for?
                              </p>
                              <div className="d-flex gap-2">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="link-more p-0"
                                  onClick={handleAdvancedSearchOpen}
                                  disabled={isLoading || isSubmitting}
                                  type="button"
                                >
                                  Advanced Creator Search
                                </Button>
                                <span className="text-muted">•</span>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() =>
                                    handleToggleInviteMode(
                                      true,
                                      setFieldValue,
                                      setFieldTouched,
                                      setFieldError,
                                    )
                                  }
                                  disabled={isLoading || isSubmitting}
                                  type="button"
                                >
                                  Add New Creator
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex align-items-center justify-content-between p-2">
                              <span className="text-muted">
                                Inviting new creator...
                              </span>
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                title="Return to creator selection"
                                onClick={() =>
                                  handleToggleInviteMode(
                                    false,
                                    setFieldValue,
                                    setFieldTouched,
                                    setFieldError,
                                  )
                                }
                                disabled={isLoading || isSubmitting}
                                type="button"
                              >
                                Use existing creator
                              </Button>
                            </div>
                          )}
                          <ErrorMessage
                            name="creator_id"
                            component="div"
                            className="text-danger"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="campaign_id">Campaign</Label>
                          <CampaignSelect
                            name="campaign_id"
                            isDisabled={
                              isLoading || isSubmitting || !!campaign_id
                            }
                            required={true}
                            creator_id={
                              !isInviteMode
                                ? values.creator_id || creator_id
                                : undefined
                            }
                            onChange={(selectedOption) =>
                              !isInviteMode
                              && handleCampaignSelectChange(
                                selectedOption,
                                setFieldError,
                                setFieldTouched,
                                values.creator_id,
                              )
                            }
                          />
                          <ErrorMessage
                            name="campaign_id"
                            component="div"
                            className="text-danger"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Creator invitation fields */}
                    {isInviteMode && (
                      <>
                        <Alert color="info">
                          <h5 className="alert-heading mb-3">
                            Invite and assign new creator
                          </h5>
                          <p className="mb-2">
                            You have selected to invite a new creator to the
                            portal when creating this assignment. Please fill in
                            the creator&apos;s details below.
                          </p>
                          <p className="mb-2">
                            This means that the creator will be sent an
                            invitation email to sign up for the Creator Payment
                            Portal and be assigned to a campaign at the same
                            time in just a single step.
                          </p>

                          <p className="mb-1">
                            If you want to invite a creator to the portal
                            without assigning them to a campaign, you can do so
                            from the{" "}
                            <NavLink
                              to={`/creators`}
                              className="alert-link text-decoration-underline d-inline"
                            >
                              Creators
                            </NavLink>{" "}
                            page.
                          </p>
                        </Alert>
                        <CreatorInviteFields
                          disabled={isLoading || isSubmitting}
                        />
                        <hr className="mb-4" />
                      </>
                    )}

                    {/* duplicate assignment error display */}
                    {hasDuplicateError
                      && !isInviteMode
                      && values.creator_id
                      && values.campaign_id
                      && !isValidatingDuplicate && (
                      <Row>
                        <Col>
                          <div
                            className="alert alert-warning d-flex align-items-center"
                            role="alert"
                          >
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <div>
                                Duplicate assignment - Creator already assigned
                                to this campaign.
                              {duplicateError.replace(
                                "__DUPLICATE_ASSIGNMENT__",
                                "",
                              ) !== "no-id" ? (
                                  <>
                                    {" "}
                                    <NavLink
                                      to={`/assignments/${duplicateError.replace("__DUPLICATE_ASSIGNMENT__", "")}`}
                                      className="alert-link text-decoration-underline"
                                    >
                                      View existing assignment
                                    </NavLink>
                                  </>
                                ) : (
                                  <>
                                    {" "}
                                    Please check for an existing assignment
                                    manually.{" "}
                                    <NavLink
                                      to="/assignments"
                                      className="alert-link text-decoration-underline"
                                    >
                                      View all assignments
                                    </NavLink>
                                  </>
                                )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="rate">
                            <CreatorVisibleIndicator
                              isVisible={true}
                              showIcon={true}
                              iconPosition="static"
                            >
                              Rate
                            </CreatorVisibleIndicator>
                          </Label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <Input
                              id="rate"
                              name="rate"
                              type="number"
                              step="0.01"
                              min="0"
                              value={values.rate}
                              onChange={handleChange}
                              invalid={!!(errors.rate && touched.rate)}
                              disabled={isLoading || isSubmitting}
                              placeholder="0.00"
                            />
                          </div>
                          <ErrorMessage
                            name="rate"
                            component="div"
                            className="text-danger"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="status">
                            <CreatorVisibleIndicator
                              isVisible={true}
                              showIcon={true}
                              iconPosition="static"
                            >
                              Status
                            </CreatorVisibleIndicator>
                          </Label>
                          <Input
                            id="status"
                            name="status"
                            type="text"
                            value={values.status}
                            onChange={handleChange}
                            invalid={!!(errors.status && touched.status)}
                            disabled={isLoading || isSubmitting}
                            placeholder="Enter status/notes (optional)"
                          />
                          <ErrorMessage
                            name="status"
                            component="div"
                            className="text-danger"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="nextPayoutDate">
                            <CreatorVisibleIndicator
                              isVisible={true}
                              showIcon={true}
                              iconPosition="static"
                            >
                              Expected Pay Date
                            </CreatorVisibleIndicator>
                          </Label>
                          <Input
                            id="nextPayoutDate"
                            name="nextPayoutDate"
                            type="date"
                            value={values.nextPayoutDate}
                            onChange={handleChange}
                            disabled={isLoading || isSubmitting}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="agencyCreatorName">
                            Custom Handle
                          </Label>
                          <Input
                            id="agencyCreatorName"
                            name="agencyCreatorName"
                            type="text"
                            value={values.agencyCreatorName}
                            onChange={handleChange}
                            disabled={isLoading || isSubmitting}
                            placeholder="Optional custom name for Agency or Creator"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>

                  <AssignmentAdvancedCreatorSearchWrapper
                    isOpen={isAdvancedSearchOpen}
                    onClose={handleAdvancedSearchClose}
                    campaign_id={campaign_id}
                  />
                </>
              );
            }}
          </Formik>
        )}
      </ModalBody>

      <ModalFooter>
        {isAssignmentCreated ? (
          <>
            {createdAssignmentData && (
              <NavLink to={`/assignments/${createdAssignmentData.result?._id}`}>
                <Button color="primary">View Assignment</Button>
              </NavLink>
            )}
            <Button color="secondary" onClick={handleAddAnother}>
              Add Another Assignment
            </Button>
            <Button color="dark" onClick={handleClose}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Button color="secondary" outline onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="dark"
              type="submit"
              form="assignment-form"
              disabled={isLoading || isValidatingDuplicate}
            >
              Create Assignment
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
